// frontend/src/pages/admin/ManageArticlesPage.tsx
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  FileText,
  PlusCircle,
  MoreHorizontal,
  Search,
  Edit3,
  Trash2,
  Eye,
  Loader2,
  FileWarning,
  AlertCircle,
  LayoutDashboard,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

import {
  type Article as ArticleAdminView,
  type FetchArticlesParams,
} from "@/types";
// Import the specific deleteArticle function and rename it to avoid conflict if needed
import {
  getArticles,
  deleteArticle as apiDeleteArticle,
} from "@/api/articleService";

const ManageArticlesPage = () => {
  const [articles, setArticles] = useState<ArticleAdminView[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true); // General loading for the page
  const [isDeleting, setIsDeleting] = useState<number | null>(null); // To track which article is being deleted
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Fetch articles when the component mounts or searchTerm changes
  useEffect(() => {
    const fetchAdminArticles = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const params: FetchArticlesParams = {
          status: "all", // Admin should fetch all statuses
        };
        if (searchTerm) {
          // Client-side search for now
        }
        // params.status = 'all'; // Fetch all statuses for admin

        const fetchedArticles = await getArticles(params);

        let articlesToDisplay = fetchedArticles;
        if (searchTerm) {
          articlesToDisplay = fetchedArticles.filter(
            (article) =>
              article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
              (article.author_name &&
                article.author_name
                  .toLowerCase()
                  .includes(searchTerm.toLowerCase())) ||
              article.tags.some((tag) =>
                tag.name.toLowerCase().includes(searchTerm.toLowerCase())
              )
          );
        }
        setArticles(articlesToDisplay);
      } catch (err: any) {
        if (err.response && err.response.status === 401) {
          setError("Unauthorized. Please log in again.");
          localStorage.removeItem("authToken");
          navigate("/login?sessionExpired=true");
        } else {
          setError("Failed to fetch articles. Please try again later.");
        }
        console.error("Error fetching admin articles:", err);
        setArticles([]);
      } finally {
        setIsLoading(false);
      }
    };
    const debounceTimeout = setTimeout(() => {
      fetchAdminArticles();
    }, 300);
    return () => clearTimeout(debounceTimeout);
  }, [searchTerm, navigate]);

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getStatusBadgeVariant = (
    status: ArticleAdminView["status"]
  ): "default" | "secondary" | "outline" | "destructive" => {
    switch (status.toLowerCase()) {
      case "published":
        return "default";
      case "draft":
        return "secondary";
      case "archived":
        return "outline";
      default:
        return "secondary";
    }
  };

  // --- UPDATED handleDeleteArticle ---
  const handleDeleteArticle = async (articleId: number) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this article? This action cannot be undone."
      )
    ) {
      return;
    }
    setIsDeleting(articleId); // Set loading state for this specific article
    setError(null); // Clear previous errors
    try {
      await apiDeleteArticle(articleId);
      setArticles((prevArticles) =>
        prevArticles.filter((article) => article.id !== articleId)
      );
      // Consider using a toast notification for better UX
      alert("Article deleted successfully.");
    } catch (err: any) {
      console.error("Failed to delete article:", err);
      // Display error to user (e.g., using a toast notification)
      if (err.response && err.response.data && err.response.data.detail) {
        setError(`Delete failed: ${err.response.data.detail}`);
        alert(`Delete failed: ${err.response.data.detail}`);
      } else {
        setError("Failed to delete article. Please try again.");
        alert("Failed to delete article. Please try again.");
      }
    } finally {
      setIsDeleting(null); // Clear loading state
    }
  };

  // --- LOADING AND ERROR STATES FOR THE PAGE ---
  if (isLoading && articles.length === 0) {
    // Show initial loading only if no articles are yet displayed
    return (
      <div className="container mx-auto px-4 py-20 flex flex-col items-center justify-center min-h-[60vh]">
        <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
        <p className="text-lg text-muted-foreground">Loading articles...</p>
      </div>
    );
  }

  if (error && articles.length === 0) {
    // Show page-level error only if it prevented initial load
    return (
      <div className="container mx-auto px-4 py-20 text-center min-h-[60vh] flex flex-col justify-center items-center">
        <FileWarning className="h-16 w-16 text-destructive mx-auto mb-6" />
        <h2 className="text-2xl font-semibold text-destructive mb-2">
          Error Loading Articles
        </h2>
        <p className="text-muted-foreground mb-6">{error}</p>
        <Button
          onClick={() => {
            setIsLoading(true);
            fetchAdminArticles();
          }}
          variant="outline"
        >
          Try Again
        </Button>
      </div>
    );
  }
  // Helper function to re-trigger fetch, needed for Try Again button
  const fetchAdminArticles = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params: FetchArticlesParams = {
          status: "all", // Admin should fetch all statuses
        };
      // Re-apply filters if any
      const fetchedArticles = await getArticles(params);
      setArticles(fetchedArticles);
    } catch (err) {
      setError("Failed to fetch articles. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 md:py-12 space-y-8 text-foreground bg-background">
      <header className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b border-border/60 pb-6 relative">
        {/* ... header content ... */}
        <div className="absolute bottom-0 left-0 w-1/3 h-px bg-gradient-to-r from-primary/70 via-accent/50 to-transparent"></div>
        <div className="flex items-center">
          <FileText className="h-10 w-10 text-primary mr-4 flex-shrink-0" />
          <div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
              Manage{" "}
              <span className="text-gradient-maroon-to-gold">Articles</span>
            </h1>
            <p className="mt-1 text-muted-foreground">
              Create, edit, and organize all site articles.
            </p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center mt-4 sm:mt-0">
          {" "}
          {/* Added mt-4 sm:mt-0 for spacing */}
          <Button
            asChild
            variant="outline"
            className="border-primary text-primary hover:bg-primary/10 hover:text-primary transition-colors self-start sm:self-center w-full sm:w-auto"
          >
            <Link to="/admin/dashboard">
              <LayoutDashboard className="mr-2 h-4 w-4" /> Dashboard
            </Link>
          </Button>
          <Button
            asChild
            className="!bg-gradient-to-r !from-primary !to-accent hover:!from-primary/90 hover:!to-accent/90 text-primary-foreground shadow-md hover:shadow-lg transition-all duration-300 w-full sm:w-auto"
          >
            <Link to="/admin/articles/new">
              {" "}
              {/* Link specific to the page */}
              <PlusCircle className="mr-2 h-5 w-5" /> Add New Article{" "}
              {/* Text specific to the page */}
            </Link>
          </Button>
        </div>
      </header>

      <div className="flex flex-col sm:flex-row items-center gap-4">
        <div className="relative w-full sm:max-w-sm">
          <Input
            type="search"
            placeholder="Search articles by title, author, tag..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 border-border focus:border-primary focus:ring-primary"
          />
          <Search className="absolute left-3 top-1/2 h-5 w-5 text-muted-foreground -translate-y-1/2" />
        </div>
      </div>

      {/* Display delete error if any */}
      {error &&
        !isLoading &&
        articles.length > 0 && ( // Show non-critical errors here, e.g., delete error
          <div className="p-3 bg-destructive/10 text-destructive border border-destructive/30 rounded-md flex items-center">
            <AlertCircle className="h-5 w-5 mr-2" /> {error}
          </div>
        )}

      <Card className="shadow-lg bg-card border border-border/30 pt-0">
        <CardContent className="p-0">
          <Table>
            <TableHeader className=" bg-vedasMaroon-800">
              <TableRow className="border-border/50">
                <TableHead className="w-[35%] text-primary-foreground py-4">
                  Title
                </TableHead>
                <TableHead className="text-primary-foreground">
                  Author
                </TableHead>
                <TableHead className="text-primary-foreground">
                  Status
                </TableHead>
                <TableHead className="text-primary-foreground">
                  Last Updated
                </TableHead>
                <TableHead className="text-right text-primary-foreground">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && articles.length === 0 ? ( // This specific check might be redundant due to page level loader
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="text-center py-16 text-muted-foreground"
                  >
                    <div className="flex flex-col items-center justify-center">
                      <Loader2 className="h-8 w-8 animate-spin text-primary mb-3" />
                      Loading articles...
                    </div>
                  </TableCell>
                </TableRow>
              ) : articles.length > 0 ? (
                articles.map((article) => (
                  <TableRow
                    key={article.id}
                    className="border-border/40 hover:bg-muted/30 dark:hover:bg-muted/10"
                  >
                    <TableCell className="font-medium text-foreground">
                      <Link
                        to={`/articles/${article.slug}`}
                        className="hover:text-primary hover:underline transition-colors"
                        title="Edit article"
                      >
                        {article.title}
                      </Link>
                      <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-1">
                        {article.tags.slice(0, 3).map((tag) => (
                          <Badge
                            key={tag.id}
                            variant="outline"
                            className="px-1.5 py-0.5 border-border/70 text-muted-foreground"
                          >
                            {tag.name}
                          </Badge>
                        ))}
                        {article.tags.length > 3 && (
                          <Badge
                            variant="outline"
                            className="px-1.5 py-0.5 border-border/70 text-muted-foreground"
                          >
                            +{article.tags.length - 3}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {article.author_name || "N/A"}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={getStatusBadgeVariant(
                          article.status as ArticleAdminView["status"]
                        )}
                        className="capitalize"
                      >
                        {article.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(article.updated_at)}
                    </TableCell>
                    <TableCell className="!items-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger
                          className="justify-self-end"
                          asChild
                        >
                          {isDeleting === article.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <MoreHorizontal className="h-4 w-4 cursor-pointer" />
                          )}
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="end"
                          className="bg-popover text-popover-foreground border-border"
                        >
                          <DropdownMenuLabel>Actions</DropdownMenuLabel>
                          <DropdownMenuItem
                            asChild
                            className="hover:bg-muted/50 dark:hover:bg-muted/20 focus:bg-muted/50 dark:focus:bg-muted/20 cursor-pointer"
                          >
                            <Link
                              to={`/articles/${article.slug}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <Eye className="mr-2 h-4 w-4 text-primary/80" />{" "}
                              View Live
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            asChild
                            className="hover:bg-muted/50 dark:hover:bg-muted/20 focus:bg-muted/50 dark:focus:bg-muted/20 cursor-pointer"
                          >
                            <Link to={`/admin/articles/edit/${article.id}`}>
                              <Edit3 className="mr-2 h-4 w-4 text-primary/80" />{" "}
                              Edit
                            </Link>
                          </DropdownMenuItem>

                          <DropdownMenuSeparator className="bg-border/50" />
                          <DropdownMenuItem
                            onClick={() => handleDeleteArticle(article.id)}
                            className="text-destructive focus:text-destructive-foreground focus:bg-destructive/90 cursor-pointer"
                            disabled={isDeleting === article.id}
                          >
                            {isDeleting === article.id ? (
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                              <Trash2 className="mr-2 h-4 w-4" />
                            )}
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="text-center py-16 text-muted-foreground"
                  >
                    No articles found.
                    {searchTerm && " Try adjusting your search or clearing it."}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default ManageArticlesPage;
